import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";
import { logSiteEvent } from "@/lib/site-events";
import { CheckCircle, Mail } from "lucide-react";
import { SubstackCTA } from "@/components/SubstackCTA";

interface NewsletterSignupProps {
  source?: string;
  variant?: "footer" | "card";
  className?: string;
}

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewsletterSignup = ({ source = "footer", variant = "footer", className = "" }: NewsletterSignupProps) => {
  // Landmark labels must be unique per page (two signups can appear at once).
  const formLabel = `Newsletter signup (${source})`;
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);

  const onDark = variant === "footer";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!emailRe.test(clean) || clean.length > 255) {
      toast({ title: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("newsletter-subscribe", {
        body: { email: clean, firstName: firstName.trim() || undefined, source },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setDone(true);
      setAlreadyConfirmed(!!data?.alreadyConfirmed);
      trackEvent("newsletter_signup", { source, already_confirmed: !!data?.alreadyConfirmed });
      logSiteEvent("newsletter_signup", undefined, source);
      toast({
        title: data?.alreadyConfirmed ? "You're already subscribed" : "Check your inbox",
        description: data?.alreadyConfirmed
          ? "This email is already receiving our caregiver updates."
          : "We sent a confirmation link to finish your signup.",
      });
    } catch (err) {
      toast({
        title: "We couldn't sign you up",
        description: err instanceof Error ? err.message : "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className={`flex items-start gap-3 text-sm ${onDark ? "text-primary-foreground/80" : "text-muted-foreground"} ${className}`}>
        <CheckCircle className="h-5 w-5 shrink-0 mt-0.5" />
        <p>
          {alreadyConfirmed
            ? "You're already subscribed — no confirmation needed. Watch your inbox for our next caregiver resource note."
            : "Almost there — check your email and click the confirmation link to start receiving caregiver resources."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${className}`} aria-label={formLabel}>
      <div className="flex flex-col sm:flex-row gap-2">
        <label htmlFor={`newsletter-name-${source}`} className="sr-only">First name (optional)</label>
        <Input
          id={`newsletter-name-${source}`}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name (optional)"
          maxLength={100}
          autoComplete="given-name"
          className={onDark ? "bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50" : ""}
        />
        <label htmlFor={`newsletter-email-${source}`} className="sr-only">Email address</label>
        <Input
          id={`newsletter-email-${source}`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          maxLength={255}
          autoComplete="email"
          className={onDark ? "bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50" : ""}
        />
        <Button type="submit" disabled={loading} className="shrink-0">
          <Mail className="h-4 w-4 mr-2" aria-hidden="true" />
          {loading ? "Sending…" : "Sign up"}
        </Button>
      </div>
      <p className={`text-xs ${onDark ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
        Occasional caregiver resources and local Nashville guides. No selling, no spam — unsubscribe anytime.
      </p>
      <div className="pt-2">
        <SubstackCTA variant="inline" location={`newsletter_${source}`} onDark={onDark} />
      </div>
    </form>
  );
};

export default NewsletterSignup;
